import React, { useEffect, useState } from "react";
import { adminAxios, errText } from "../AuthContext";
import { Input, Textarea, Toggle, FileUploader } from "./FormFields";
import { Eye, EyeOff, Save, CheckCircle2, XCircle, Loader2, Cloud, Globe, Move3d, Info } from "lucide-react";

const DEFAULT = {
    enabled: false, space_id: "", sdk_key: "", mpskin_url: "",
    cloud_token_id: "", cloud_token_secret: "",
    title: "Gemello Digitale",
    description: "Esplora Tadasuni in 3D con il nostro tour virtuale interattivo",
    thumbnail: "",
};

const MaskedInput = ({ label, value, onChange, testid, placeholder, hint }) => {
    const [show, setShow] = useState(false);
    return (
        <div>
            <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-1.5">{label}</label>
            <div className="relative">
                <input
                    type={show ? "text" : "password"}
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-stone-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none text-sm bg-white font-mono"
                    placeholder={placeholder}
                    data-testid={testid}
                />
                <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 hover:text-emerald-700 p-1" aria-label="Toggle visibility">
                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
            </div>
            {hint && <div className="text-xs text-stone-500 mt-1">{hint}</div>}
        </div>
    );
};

export default function BorgoSettingsPanel() {
    const [cfg, setCfg] = useState(DEFAULT);
    const [busy, setBusy] = useState(true);
    const [saveMsg, setSaveMsg] = useState("");
    const [testMsg, setTestMsg] = useState(null); // { ok: bool, message: str }
    const [testing, setTesting] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const { data } = await adminAxios().get("/admin/settings/borgo-digital-twin");
            setCfg({ ...DEFAULT, ...data });
        } catch {}
        setBusy(false);
    };
    useEffect(() => { load(); }, []);

    const update = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

    const save = async () => {
        setSaving(true); setSaveMsg("");
        try {
            await adminAxios().put("/admin/settings/borgo-digital-twin", cfg);
            setSaveMsg("✓ Configurazione salvata");
            setTimeout(() => setSaveMsg(""), 3000);
        } catch (e) {
            setSaveMsg("✗ Errore salvataggio");
        } finally { setSaving(false); }
    };

    const testConn = async () => {
        setTesting(true); setTestMsg(null);
        try {
            const { data } = await adminAxios().post("/admin/settings/matterport-test", {
                token_id: cfg.cloud_token_id, token_secret: cfg.cloud_token_secret,
            });
            setTestMsg(data);
        } catch (e) {
            setTestMsg({ ok: false, message: errText(e, "Errore") });
        } finally { setTesting(false); }
    };

    if (busy) return <div className="text-center py-12"><Loader2 size={24} className="animate-spin mx-auto text-blue-600" /></div>;

    return (
        <div className="space-y-6" data-testid="borgo-settings-panel">
            <div>
                <h2 className="font-serif text-2xl font-semibold text-stone-900 mb-1">Digital Twin del Borgo</h2>
                <p className="text-sm text-stone-500">Configura il tour 3D visibile sulla pagina pubblica Gemello Digitale.</p>
            </div>

            {/* ============ Card 1 · Matterport / MPSKIN Viewer ============ */}
            <div className="bg-white rounded-2xl border border-stone-200 p-6 md:p-8">
                <div className="flex items-start gap-3 mb-5">
                    <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center">
                        <Move3d size={18} className="text-pink-600" />
                    </div>
                    <div>
                        <h3 className="font-serif text-lg font-semibold text-stone-900">Configurazione Matterport</h3>
                        <p className="text-sm text-stone-500">Configura lo spazio 3D Matterport per il digital twin</p>
                    </div>
                </div>

                <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3">
                    <Toggle label="Mostra il badge LIVE 3D sulla home" value={cfg.enabled} onChange={(v) => update("enabled", v)} testid="dt-enabled" />
                    <div className="text-[11px] text-stone-600 mt-1.5">
                        💡 <b>Il tour digitale è visibile sul sito pubblico non appena compili Space ID o MPSKIN URL qui sotto</b> — indipendentemente da questo interruttore. Questo toggle serve solo a mostrare/nascondere il badge animato &quot;LIVE 3D&quot; nella preview della home.
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-1.5">Space ID *</label>
                        <input
                            type="text"
                            value={cfg.space_id}
                            onChange={(e) => update("space_id", e.target.value)}
                            placeholder="j1r4zUjanif"
                            className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none text-sm bg-white font-mono"
                            data-testid="dt-space-id"
                        />
                        <div className="text-xs text-stone-500 mt-1">L'ID dello spazio Matterport (dalla URL: <span className="font-mono">my.matterport.com/show/?m=<b>ID</b></span>)</div>
                    </div>
                    <MaskedInput
                        label="SDK Key"
                        value={cfg.sdk_key}
                        onChange={(v) => update("sdk_key", v)}
                        testid="dt-sdk-key"
                        placeholder="59wwqhip77fxkqiurcae74fed"
                        hint={<>Ottieni la SDK Key dal <a href="https://static.matterport.com/showcase-sdk/latest/docs/js/index.html" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Matterport Developer Portal</a></>}
                    />
                </div>

                <div className="mt-5">
                    <div className="flex items-center gap-2 mb-1.5">
                        <Globe size={14} className="text-purple-600" />
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">MPSKIN Tour URL <span className="normal-case text-stone-400 font-normal">(opzionale)</span></label>
                    </div>
                    <input
                        type="url"
                        value={cfg.mpskin_url}
                        onChange={(e) => update("mpskin_url", e.target.value)}
                        placeholder="https://tour.fairsgate.com/it/tour/xxx"
                        className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none text-sm bg-white"
                        data-testid="dt-mpskin-url"
                    />
                    <div className="text-xs text-stone-500 mt-1">
                        Se configurato, verrà usato al posto del viewer Matterport nativo nella Dashboard. La Vista 3D continuerà ad usare Matterport nativo per la gestione POI.
                    </div>
                </div>

                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="Titolo" value={cfg.title} onChange={(v) => update("title", v)} testid="dt-title" />
                    <div className="flex items-end justify-between gap-3">
                        <div className="flex-1"><Input label="Thumbnail URL" value={cfg.thumbnail} onChange={(v) => update("thumbnail", v)} testid="dt-thumb" placeholder="/api/uploads/..." /></div>
                        <FileUploader accept="image/*" onUploaded={(u) => update("thumbnail", u.url)} testid="dt-thumb-upload" />
                    </div>
                </div>
                <div className="mt-5">
                    <Textarea label="Descrizione" value={cfg.description} onChange={(v) => update("description", v)} rows={2} testid="dt-desc" />
                </div>
            </div>

            {/* ============ Card 2 · Matterport Cloud API ============ */}
            <div className="bg-white rounded-2xl border border-stone-200 p-6 md:p-8">
                <div className="flex items-start gap-3 mb-5">
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                        <Cloud size={18} className="text-blue-600" />
                    </div>
                    <div>
                        <h3 className="font-serif text-lg font-semibold text-stone-900">Matterport Cloud API</h3>
                        <p className="text-sm text-stone-500">Configura le credenziali API per sincronizzare i POI su my.matterport.com</p>
                    </div>
                </div>

                <div className="rounded-xl bg-blue-50 border border-blue-100 p-4 mb-5 text-sm text-blue-900">
                    <div className="flex items-start gap-2">
                        <Info size={16} className="mt-0.5 flex-shrink-0" />
                        <div>
                            <div className="font-semibold mb-1">Come ottenere le credenziali:</div>
                            <ol className="list-decimal ml-4 space-y-0.5 text-blue-800">
                                <li>Vai su <a className="underline" href="https://my.matterport.com" target="_blank" rel="noreferrer">my.matterport.com</a></li>
                                <li>Settings (in basso a sinistra) → Developer Tools</li>
                                <li>Nella sezione <b>API Token Management</b>, clicca "Request API keys"</li>
                                <li>Copia il <b>Token ID</b> e il <b>Token Secret</b> (mostrato solo una volta!)</li>
                            </ol>
                            <div className="mt-2 flex items-center gap-1 text-amber-700 font-semibold">
                                ⚠ Il Token Secret viene mostrato solo una volta. Salvalo subito!
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <MaskedInput label="Token ID" value={cfg.cloud_token_id} onChange={(v) => update("cloud_token_id", v)} testid="dt-token-id" placeholder="24d3cb54e67b9246" />
                    <MaskedInput label="Token Secret" value={cfg.cloud_token_secret} onChange={(v) => update("cloud_token_secret", v)} testid="dt-token-secret" placeholder="c6d7f2b0920b8629fb1d4fd1907e299a" />
                </div>

                <div className="mt-5 flex items-center gap-3 flex-wrap">
                    <button
                        onClick={testConn}
                        disabled={testing || !cfg.cloud_token_id || !cfg.cloud_token_secret}
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="dt-test-connection"
                    >
                        {testing ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                        {testing ? "Test in corso..." : "Testa Connessione"}
                    </button>
                    {testMsg && (
                        <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold ${testMsg.ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`} data-testid="dt-test-result">
                            {testMsg.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                            {testMsg.message}
                        </div>
                    )}
                </div>
            </div>

            {/* ============ Save bar ============ */}
            <div className="sticky bottom-0 bg-white border border-stone-200 rounded-2xl p-4 shadow-lg flex items-center justify-between flex-wrap gap-3">
                <div className="text-sm text-stone-600">
                    Vista pubblica: <a href="/#/gemello-digitale" target="_blank" rel="noreferrer" className="text-emerald-700 font-semibold hover:underline">/#/gemello-digitale</a>
                </div>
                <div className="flex items-center gap-3">
                    {saveMsg && <div className={`text-sm font-semibold ${saveMsg.startsWith("✓") ? "text-emerald-700" : "text-red-700"}`} data-testid="dt-save-msg">{saveMsg}</div>}
                    <button
                        onClick={save}
                        disabled={saving}
                        className="btn-primary px-6 py-2.5 rounded-full font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                        data-testid="dt-save"
                    >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        Salva Configurazione
                    </button>
                </div>
            </div>
        </div>
    );
}
